import React, { useState } from "react";
import { motion } from "framer-motion";
import api from "../../utils/api";

const DownloadRequestModal = ({ payslip, userId, onClose, onSuccess  }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'

  function formatSalaryPeriod(period) {
  if (!period) return "";

  const [year, month] = period.split("-");
  const date = new Date(year, month - 1);

  return date.toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
}


  const handleSubmit = async () => {
    if (!reason.trim()) {
      setMessage("Please provide a reason for download request");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(
        `/payslip/apply-download-request/${payslip.payid}?employeeId=${userId}`,
        { reason }
      );
      onSuccess(payslip.payid);

      setMessage("Download request sent successfully!");
      setMessageType("success");
      setReason("");
      
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (error) {
      setMessage(error.response?.data || "Failed to send request. Please try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="relative bg-white rounded-2xl shadow-2xl w-[95%] sm:w-[450px] p-4 sm:p-6 z-50"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50"
        >
          <svg
            className="w-6 h-6 text-slate-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800">
            Download Request
          </h2>
          
        </div>

        {/* Payslip Info */}
        <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-slate-600">Salary Period</p>
              <p className="text-base font-semibold text-slate-800">
                {formatSalaryPeriod(payslip.salaryPeriod)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-600">Net Payable</p>
              <p className="text-base font-semibold text-green-600">
                ₹{payslip.netPayable}
              </p>
            </div>
          </div>
        </div>

        {/* Reason Input */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-slate-700 mb-2">
            Reason for Download <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={loading}
            placeholder="Please provide a reason for requesting the payslip download"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-slate-100 disabled:cursor-not-allowed"
            rows="3"
          />
          
        </div>

        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 p-3 rounded-lg text-sm ${
              messageType === "success"
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-red-50 border border-red-200 text-red-700"
            }`}
          >
            <p className="font-medium">{message}</p>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <motion.button
            onClick={onClose}
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-full font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </motion.button>
          <motion.button
            onClick={handleSubmit}
            disabled={loading || !reason.trim()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-3 py-2 text-sm bg-slate-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  ></path>
                </svg>
                Sending...
              </>
            ) : (
              "Send Request"
            )}
          </motion.button>
        </div>
       
      </motion.div>
    </div>
  );
};

export default DownloadRequestModal;
